<template>
  <div class="raw-charges-tab">
    <div class="raw-charges-tab-data-table w-6">
      <h2 class="section-subheader">{{ t('rateShop.charges.charges', 2) }}</h2>

      <DataTable
        :value="rateQuote.rawChargesRedesign"
        :row-class="getRowClass"
        class="rate-detail-table p-datatable-sm"
        auto-layout
      >
        <Column :header="t('rateShop.charges.code')" field="code" class="w-1">
          <template #body="{ data }">
            <div
              :class="{ 'font-bold': !LINEHAUL_DISCOUNT_GROUP.has(data.code) }"
            >
              {{ data.code }}
            </div>

            <div
              v-if="
                data.description &&
                lowerCase(data.code) !== lowerCase(data.description)
              "
              class="text-gray-darker sub-heading"
            >
              {{ data.description }}
            </div>
          </template>
        </Column>

        <Column
          :header="t('rateShop.charges.stop')"
          field="stopSequenceNumber"
          class="w-1"
        >
          <template #body="{ data }">
            <StopSequenceNumberTooltip :data="data" />
          </template>
        </Column>

        <Column :header="t('rateShop.charges.buyRate')" class="w-1">
          <template #body="{ data: rateQuoteCharge }">
            <div>
              {{
                renderMoney(rateQuoteCharge.charge, {
                  currencyCode: rateQuote.currencyCode,
                })
              }}
            </div>
          </template>

          <template #footer>
            <div class="text-success font-medium">
              {{
                renderMoney(rateQuote.rawTotalPrice, {
                  currencyCode: rateQuote.currencyCode,
                })
              }}
            </div>
          </template>
        </Column>

        <Column class="w-1">
          <template #header>
            <span
              class="p-datatable-column-title"
              data-pc-section="columntitle"
            >
              {{ t('rateShop.charges.margin') }}
            </span>
            <InfoIcon
              class="text-gray-darker"
              position="bottom"
              :tooltip="t('rateShop.charges.marginShownIsUplift')"
            />
          </template>

          <template #body="{ data: rateQuoteCharge }">
            <div>
              {{
                renderMoney(rateQuoteCharge.margin, {
                  currencyCode: rateQuote.currencyCode,
                })
              }}
            </div>
          </template>

          <template #footer>
            <div class="text-success font-medium">
              {{
                renderMoney(totalMarginAmount, {
                  currencyCode: rateQuote.currencyCode,
                })
              }}
            </div>
          </template>
        </Column>

        <Column :header="t('rateShop.charges.sellRate')" class="w-1">
          <template #body="{ data: rateQuoteCharge }">
            <div>
              {{
                renderMoney(rateQuoteCharge.sell, {
                  currencyCode: rateQuote.currencyCode,
                })
              }}
            </div>
          </template>

          <template #footer>
            <div class="text-success font-medium">
              {{
                renderMoney(rateQuote.totalPrice, {
                  currencyCode: rateQuote.currencyCode,
                })
              }}
            </div>
          </template>
        </Column>
      </DataTable>

      <div class="p-data-table-uplift-details">
        <h2 class="section-subheader">
          {{ t('rateShop.details.lineHaulUpliftDetails') }}
        </h2>
        <DataTable
          :value="collectRateQuoteUpliftDetails(rateQuote)"
          class="rate-detail-table p-datatable-sm datatable-no-header"
          auto-layout
        >
          <Column field="k" header-style="display:none;" />
          <Column field="v" header-style="display:none;" />
        </DataTable>
      </div>
    </div>

    <div class="quote-details w-6">
      <RateQuoteDetails :rate-quote="rateQuote" :email-tender="emailTender" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { lowerCase, renderMoney } from '@rygen/rygen-ui/i18n';

import { RateQuote, RateQuoteCharge } from '@/model';

import RateQuoteDetails from '@/components/rateShop/expandedDetails/RateQuoteDetails.vue';
import StopSequenceNumberTooltip from '@/components/rateShop/expandedDetails/StopSequenceNumberTooltip.vue';
import InfoIcon from '@rygen/rygen-ui/components/ui/InfoIcon.vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';

const props = defineProps({
  rateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  emailTender: {
    type: Boolean,
    default: () => false,
  },
});
const { rateQuote, emailTender } = toRefs(props);

const { t } = useI18n();

const LINEHAUL_DISCOUNT_GROUP = new Set([
  'LINEHAUL_GROSS',
  'LINEHAUL_DISCOUNT',
]);

const getRowClass = (data: RateQuoteCharge) => {
  return LINEHAUL_DISCOUNT_GROUP.has(data.code)
    ? 'discount-row-grouped'
    : undefined;
};

const totalMarginAmount = computed(() => {
  return (
    rateQuote.value.rawChargesRedesign
      .map((rate) => rate.margin)
      .reduce((total, curr) => (total ?? 0) + (curr ?? 0), 0) ?? 0
  );
});

const collectRateQuoteUpliftDetails = (
  rateQuote: RateQuote,
): { k: string; v: string }[] => {
  const details: { k: string; v: string }[] = [];
  details.push({
    k: t('rateShop.details.upliftDetails'),
    v: renderMoney(rateQuote.upliftAmount, {
      currencyCode: rateQuote.currencyCode,
    }),
  });
  if (rateQuote.upliftRule?.upliftRate) {
    details.push({
      k: t('rateShop.details.upliftRate'),
      v: `${rateQuote.upliftRule.upliftRate} ${rateQuote.upliftRule.upliftRateUnit}`,
    });
  }
  details.push({
    k: t('rateShop.details.upliftMinMax'),
    v:
      renderMoney(rateQuote.upliftRule?.minimumAmount, {
        currencyCode: rateQuote.currencyCode,
      }) +
      ' / ' +
      renderMoney(rateQuote.upliftRule?.maximumAmount, {
        currencyCode: rateQuote.currencyCode,
        defaultValue: '∞',
      }),
  });
  return details;
};
</script>

<style>
.raw-charges-tab {
  display: flex;
  gap: var(--sp-3);

  .rate-detail-table {
    .discount-row-grouped > td {
      border-bottom: none;
    }
  }

  .quote-details {
    border-left: 1px solid var(--divider-color);
    padding-inline: var(--sp-3);
  }
}
</style>
